import { store } from "@app/store"
import { ProgramManualUnlockClient } from "@clients/ProramManualUnlockClient"
import { UsersClient } from "@clients/usersClient"
import { editFeatureFlagsPopupOpened } from "@features/account-management/accountManagementSlice"
import {
  editRegistrationLanguagePopupOpened,
  editStudentCareerSuccessAdvisorPopupOpened,
  editStudentDetailsPopupOpened,
  editStudentMentorPopupOpened,
  offboardStudentPopupOpened,
  openEditDatacampCredentialsPopup,
  registerStudentToCourseUpdated,
  unlockNextWeekPopupUpdated,
  unregisterStudentFromCourseUpdated,
} from "@features/program/programSlice"
import {
  showErrorSnackbar,
  showSnackbarItem,
  showSuccessSnackbar,
  SnackbarDuration,
} from "@features/ui/uiSlice"

// THE canonical set of things staff can do to a student. Surfaces render these
// their own way (kebab, grid menu, panel) but they all read from this one list —
// so an action can never exist on one screen and be missing on another, which is
// exactly how "Register to a course" ended up unreachable from the students page.
export type StudentActionContext = {
  studentId: string
  name: string
  firstName: string
  lastName: string
  email: string | undefined
  // Registration-scoped actions (mentor, CSA, language, drop out) need the
  // student's program/class. Absent them, those actions are omitted rather than
  // rendered dead.
  programId: string | undefined
  // Two DIFFERENT class ids, kept distinct because each is used in its own
  // place: the route's class (drop out) and the registration's class (language).
  classId: string | undefined
  registrationClassId: string | undefined
  mentorId?: string
  hasMentor: boolean
  // The students grid does not carry the CSA yet, so the popup opens without a
  // preselected advisor. It still assigns correctly.
  csaClientId?: string
  hasCsa: boolean
  programType: "legacy" | "next-gen"
}

export type StudentAction =
  | { kind: "divider"; key: string }
  | {
      kind: "item"
      key: string
      label: string
      danger?: boolean
      onClick: () => void
    }

const copyImpersonationLink = async (
  email: string | undefined,
  target: "campus" | "lms",
) => {
  if (!email) return
  await UsersClient.generateImpersonationLink(email, target)
    .then((link) => {
      navigator.clipboard.writeText(link)
      store.dispatch(
        showSuccessSnackbar(
          `${
            target === "lms" ? "LMS" : "Campus"
          } link copied. Open the URL in incognito mode.`,
        ),
      )
    })
    .catch(() => {
      store.dispatch(
        showErrorSnackbar(
          "Failed to copy impersonation link, try again later.",
        ),
      )
    })
}

export function buildStudentActions(
  ctx: StudentActionContext,
): StudentAction[] {
  const {
    studentId,
    name,
    firstName,
    lastName,
    email,
    programId,
    classId,
    registrationClassId,
    mentorId,
    hasMentor,
    csaClientId,
    hasCsa,
    programType,
  } = ctx
  const dispatch = store.dispatch
  const isLegacy = programType === "legacy"
  const mentorRole = isLegacy ? "mentor" : "ASM"

  const openUnlockNextWeekPopup = () =>
    ProgramManualUnlockClient.getUnlockableWeeks(studentId)
      .then((response) =>
        dispatch(
          unlockNextWeekPopupUpdated({
            studentId,
            firstName,
            lastName,
            viewedProgramId: programId,
            programs: response.programs,
          }),
        ),
      )
      .catch(() =>
        dispatch(
          showSnackbarItem({
            message: "Failed to load unlockable weeks",
            type: "error",
            duration: SnackbarDuration.short,
          }),
        ),
      )

  const actions: (StudentAction | undefined)[] = [
    {
      kind: "item",
      key: "editStudentDetails",
      label: "Edit student",
      onClick: () =>
        dispatch(
          editStudentDetailsPopupOpened({
            studentId,
            firstName,
            lastName,
            email: email ?? "",
          }),
        ),
    },
    programId
      ? {
          kind: "item",
          key: "assign mentor to student",
          label: hasMentor ? `Change ${mentorRole}` : `Assign ${mentorRole}`,
          onClick: () =>
            dispatch(
              editStudentMentorPopupOpened({
                studentId,
                studentName: name,
                programId,
                mentorId,
              }),
            ),
        }
      : undefined,
    programId
      ? {
          kind: "item",
          key: "assign CSA to student",
          label: hasCsa ? "Change CSA" : "Assign CSA",
          onClick: () =>
            dispatch(
              editStudentCareerSuccessAdvisorPopupOpened({
                studentId,
                studentName: name,
                programId,
                initialCareerSuccessAdvisorClientId: csaClientId,
              }),
            ),
        }
      : undefined,
    {
      kind: "item",
      key: "edit datacamp email",
      label: "Edit Datacamp email",
      onClick: () =>
        dispatch(
          openEditDatacampCredentialsPopup({
            studentId,
            studentName: `${firstName} ${lastName}`,
          }),
        ),
    },
    // Same dialog as the employee kebab in Account Management — the student id
    // is the user's client id, which is what campus feature flags are keyed by.
    {
      kind: "item",
      key: "feature-flags",
      label: "Edit feature flags",
      onClick: () =>
        dispatch(editFeatureFlagsPopupOpened({ userId: studentId })),
    },
    { kind: "divider", key: "links-divider" },
    {
      kind: "item",
      key: "impersonate",
      label: "Copy impersonation link",
      onClick: () => copyImpersonationLink(email, "campus"),
    },
    {
      kind: "item",
      key: "impersonate-lms",
      label: "Copy LMS impersonation link",
      onClick: () => copyImpersonationLink(email, "lms"),
    },
    { kind: "divider", key: "registration-divider" },
    // TRANSFER IS RETIRED. Both "Transfer to a different program" and "Transfer
    // to a different class" lived here. A transfer was only ever a REMOVE and an
    // ADD welded together — and welded badly: it ran the two as separate
    // transactions and swallowed a failure in between, which is how a student
    // ended up in NO program with their course classes cascaded away, while the
    // caller was told it worked.
    //
    // Manage programs now does both halves properly and atomically: removing a
    // program closes it with a reason (including 'graduate'), cascades its course
    // classes, ends its HubSpot enrollment and takes its Slack channels; adding
    // one carries the student's language, revives anything a previous removal
    // ended, and assigns the running and upcoming terms' classes on the spot.
    //
    // So a transfer is: add the new program, then remove the old one — in that
    // order the student is never in zero programs, and a failure leaves them in
    // BOTH (visible, fixable) rather than NONE.
    programId && registrationClassId
      ? {
          kind: "item",
          key: "change language",
          label: "Change registration language",
          onClick: () =>
            dispatch(
              editRegistrationLanguagePopupOpened({
                studentId,
                classId: registrationClassId,
                programId,
                studentName: name,
              }),
            ),
        }
      : undefined,
    !isLegacy
      ? {
          kind: "item",
          key: "register to a course",
          label: "Register to a course",
          onClick: () =>
            dispatch(
              registerStudentToCourseUpdated({
                studentId,
                firstName,
                lastName,
              }),
            ),
        }
      : undefined,
    !isLegacy
      ? {
          kind: "item",
          key: "unregister from a course",
          label: "Unregister from a course",
          onClick: () =>
            dispatch(
              unregisterStudentFromCourseUpdated({
                studentId,
                firstName,
                lastName,
              }),
            ),
        }
      : undefined,
    !isLegacy
      ? {
          kind: "item",
          key: "unlock content weeks",
          label: "Unlock content weeks",
          onClick: () => openUnlockNextWeekPopup(),
        }
      : undefined,
    isLegacy
      ? {
          kind: "item",
          key: "unlock next sprint",
          label: "Unlock next sprint",
          onClick: () => {
            ProgramManualUnlockClient.unlockNextSprint(studentId)
          },
        }
      : undefined,
    { kind: "divider", key: "danger-divider" },
    programId
      ? {
          kind: "item",
          key: "offboard",
          // Called "Offboard" for years, but it calls campus `user/school/dropout`
          // — it records a DROPOUT and marks the student churned in HubSpot. The
          // label now says what the button does. Removing someone from a single
          // program, reversibly, is Manage programs.
          label: "Drop out student",
          danger: true,
          onClick: () =>
            dispatch(
              offboardStudentPopupOpened({ programId, studentId, classId }),
            ),
        }
      : undefined,
  ]

  // Drop omitted actions, then any divider left stranded (leading, trailing or
  // doubled) once its group emptied out.
  const present = actions.filter((a): a is StudentAction => a !== undefined)
  return present.filter((action, index) => {
    if (action.kind !== "divider") return true
    const prev = present[index - 1]
    const next = present[index + 1]
    return prev !== undefined && prev.kind !== "divider" && next !== undefined
  })
}
